/**
 * Create / edit / publish / close / delete for surveys.
 *
 * Each hook invalidates the whole survey slice on success — see `useInvalidateSurveys`.
 */

import { useMutation } from "@tanstack/react-query";

import {
  closeSurvey,
  createSurvey,
  deleteSurvey,
  publishSurvey,
  updateSurvey,
} from "./surveysClient";
import type { SurveyMutationEnvelope, SurveyWriteBody } from "./surveys";
import { useInvalidateSurveys } from "./useSurveysQueries";

export function useCreateSurveyMutation() {
  const invalidate = useInvalidateSurveys();

  return useMutation<SurveyMutationEnvelope, Error, SurveyWriteBody>({
    mutationFn: (body) => createSurvey(body),
    onSuccess: (res) => invalidate(res.data?._id),
  });
}

export type UpdateSurveyVariables = {
  surveyId: string;
  body: Partial<SurveyWriteBody>;
};

export function useUpdateSurveyMutation() {
  const invalidate = useInvalidateSurveys();

  return useMutation<SurveyMutationEnvelope, Error, UpdateSurveyVariables>({
    mutationFn: ({ surveyId, body }) => updateSurvey(surveyId, body),
    onSuccess: (_res, vars) => invalidate(vars.surveyId),
  });
}

export function usePublishSurveyMutation() {
  const invalidate = useInvalidateSurveys();

  return useMutation<SurveyMutationEnvelope, Error, string>({
    mutationFn: (surveyId) => publishSurvey(surveyId),
    onSuccess: (_res, surveyId) => invalidate(surveyId),
  });
}

export function useCloseSurveyMutation() {
  const invalidate = useInvalidateSurveys();

  return useMutation<SurveyMutationEnvelope, Error, string>({
    mutationFn: (surveyId) => closeSurvey(surveyId),
    onSuccess: (_res, surveyId) => invalidate(surveyId),
  });
}

export function useDeleteSurveyMutation() {
  const invalidate = useInvalidateSurveys();

  return useMutation<SurveyMutationEnvelope, Error, string>({
    mutationFn: (surveyId) => deleteSurvey(surveyId),
    onSuccess: () => invalidate(),
  });
}
